import { queryOptions } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
} from "jsonapi-zod-query";
import { z } from "zod";
import { ownerAttributesSchema } from "@/queries/owners.ts";
import { apiUrl } from "@/utils/api.ts";

export const unitAttributesSchema = z.object({
    id: z.number(),
    rentalId: z.string(),
    unitPhone: z.string().optional(),
    rentalAgentPhone: z.string().optional(),
    codedUnit: z.string().optional(),
});

const unitSelector = createDataSelector(
    createResourceSelector({
        type: "calendar",
        attributesSchema: unitAttributesSchema,
        relationships: {
            owner: {
                resourceType: "owner",
                relationshipType: "one",
                include: {
                    type: "owner",
                    attributesSchema: ownerAttributesSchema,
                },
            },
        },
    }),
);

const unitsSelector = createResourceCollectionSelector({
    type: "calendar",
    attributesSchema: unitAttributesSchema,
    relationships: {
        owner: {
            resourceType: "owner",
            relationshipType: "one",
            include: {
                type: "owner",
                attributesSchema: ownerAttributesSchema,
            },
        },
    },
});

export type Unit = ReturnType<typeof unitSelector>;
export type PaginatedUnits = ReturnType<typeof unitsSelector>;
export type ListUnit = PaginatedUnits["data"][number];

export type ListUnitsOptions = {
    pageParams?: PageParams;
};

export const createUnitQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListUnitsOptions) =>
        queryOptions({
            queryKey: ["units", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/units");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return unitsSelector(await response.json());
            },
        }),
    get: (unitId: string) =>
        queryOptions({
            queryKey: ["unit", unitId],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/units/${unitId}`), { signal });
                await handleJsonApiError(response);
                return unitSelector(await response.json());
            },
        }),
});
